import { includes } from 'lodash';
import { FunctionComponent, useCallback, useContext, useId, useMemo, useState } from 'react';
import { DeepPartial, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { gql, useMutation, useQuery } from 'urql';
import { Loading } from '../';
import { useCurrentUser } from '../../hooks';
import styled from '../../themes/themed-styled-components';
import { MutationResult, RoleName } from '../../types';
import { HacpackMutation, HacpackMutationQsaHandlingOpinionArguments, HacpackQuery, HacpackQueryHandlingArguments, Handling, HandlingType } from '../../types/graph';
import { QueryLoaderContext } from '../QueryLoader';

const handlingQsaApprovalQuery = gql<HacpackQuery, HacpackQueryHandlingArguments>`
query handling($id: Long!) {
    handling (id: $id) {
        id
        approvedByTerminal
        fei
        cei
        standardDistance
        qsaNotes
        terminalOpinionDate
        bookingCheckResult
        loadingUnitIdentifier
        type
        checkedIn
        terminal {
            id
            name
            ceiLimit
            feiLimit
            radiusLimit
        }
    }
}`;

type HandlingRiskAssessmentProps = { handling: DeepPartial<Handling> };

const qsaHandlingOpinionMutation = gql<{ qsaHandlingOpinion: MutationResult }, HacpackMutation>`
    mutation ($handlingId: Long!, $opinion: Boolean!, $qsaNotes: String!) {
        qsaHandlingOpinion(handlingId: $handlingId, opinion: $opinion, qsaNotes: $qsaNotes)  {
            isOk
        }
    }
`;

const QsaNotesText = styled.pre`
white-space: pre-wrap
`;
const HandlingQsaApproval: FunctionComponent<HandlingRiskAssessmentProps> = ({ handling: { id } }) => {

    const { t } = useTranslation('common');
    //const { terminal } = useContext(TerminalContext);
    const { reload } = useContext(QueryLoaderContext);

    const controlId = useId();

    const [result, executeQuery] = useQuery({
        query: handlingQsaApprovalQuery,
        variables: { id: id as number },
        pause: id === undefined,
        requestPolicy: 'cache-and-network'
    });

    const { data, fetching } = result;

    const handling = data?.handling;
    const terminal = handling?.terminal;

    const form = useForm<HacpackMutationQsaHandlingOpinionArguments>({
        defaultValues: {
            //handlingId: id,
            //opinion: true,
            //qsaNotes: ''
        }
    });

    const { register, handleSubmit, formState } = form;

    const { errors } = formState;

    const [promptOpen, setPromptOpen] = useState(false);

    const togglePrompt = useCallback(() => setPromptOpen(promptOpen => !promptOpen), [setPromptOpen])
    const openPrompt = useCallback(() => setPromptOpen(true), [setPromptOpen])
    const closePrompt = useCallback(() => setPromptOpen(false), [setPromptOpen])

    const highRisk = useMemo(() => {
        if (handling === undefined || terminal === undefined || terminal === null || handling === null) {
            return null;
        }

        const { cei, fei, standardDistance } = handling;
        const { ceiLimit, feiLimit, radiusLimit } = terminal;

        if (typeof (ceiLimit) === 'number' && typeof (cei) === 'number' && ceiLimit < cei) {
            return true;
        }

        if (typeof (feiLimit) === 'number' && typeof (fei) === 'number' && feiLimit < fei) {
            return true;
        }

        if (typeof (radiusLimit) === 'number' && typeof (standardDistance) === 'number' && radiusLimit < standardDistance) {
            return true;
        }

        return false;
    }, [handling, terminal])

    const { isSuperuser, hasRoleForTerminal } = useCurrentUser();

    const [approveResult, approve] = useMutation<{ approveHandlings: MutationResult }, HacpackMutationQsaHandlingOpinionArguments>(qsaHandlingOpinionMutation);

    const { fetching: fetchingApproval } = approveResult;

    const isApproved = useMemo(() => (handling?.bookingCheckResult === true && handling?.approvedByTerminal === true) || approveResult.data?.approveHandlings?.isOk === true, [handling, approveResult]);

    const canApprove = useMemo(() => {
        if (handling === null || terminal === null || handling === undefined || terminal === undefined) {
            return false;
        }
        const { type, checkedIn, bookingCheckResult } = handling;

        if (highRisk === false || checkedIn === true || type === HandlingType.Transit || type === HandlingType.None || (bookingCheckResult !== true && type === HandlingType.Boarding) || isApproved === true || terminal === null || terminal?.id === undefined || !includes(['REEFER TERMINAL', 'VADO GATEWAY'], terminal?.name)) {
            return false;
        }

        return isSuperuser || hasRoleForTerminal(RoleName.OPERATIVE_MANAGER, terminal.id)
    }, [isSuperuser, hasRoleForTerminal, terminal, isApproved, handling, highRisk]);

    const onSubmit = useCallback<SubmitHandler<HacpackMutationQsaHandlingOpinionArguments>>((values, e) => {
        const { qsaNotes, handlingId } = values;

        if (handlingId === undefined) {
            return;
        }

        approve({ handlingId, opinion: true, qsaNotes }).then(() => {
            reload();
            executeQuery();
            closePrompt();
            //window.location.reload();
        });
    }, [approve, id, reload, executeQuery, closePrompt])

    const onError = (errors: unknown, e: unknown) => console.debug({ errors, e });

    const prompt = useMemo(() => {
        if (handling === null || terminal === null || handling === undefined || terminal === undefined) {
            return null;
        }
        const { loadingUnitIdentifier } = handling;

        return <Modal isOpen={!fetchingApproval && promptOpen} toggle={togglePrompt}>
            <form onSubmit={handleSubmit(onSubmit, onError)}>
                <ModalHeader>{t(`Approva ingresso ${loadingUnitIdentifier}`)}</ModalHeader>
                <ModalBody>
                    <Form tag="div">
                        <FormGroup>
                            <div className="d-none">
                                <input type="hidden" value={id} {...register('handlingId', {
                                    valueAsNumber: true
                                })} />
                            </div>
                            <Label for={`qsaNotes_${controlId}`}>{t('Note QSA')}</Label>
                            <textarea className="form-control" {...register('qsaNotes', {
                                required: {
                                    message: t('Questo campo � obbligatorio.'),
                                    value: true
                                }
                            })} id={`qsaNotes_${controlId}`} />
                            {errors.qsaNotes?.message !== undefined && <div className="invalid-feedback">{errors.qsaNotes.message}</div>}
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button type="submit" color="primary">{t('Conferma')}</Button>{' '}
                    <Button color="secondary" onClick={closePrompt}>{t('Annulla')}</Button>
                </ModalFooter>
            </form>
        </Modal>
    }, [t, id, handling, promptOpen, closePrompt, togglePrompt, fetchingApproval, register, handleSubmit, onSubmit, onError, errors, controlId])

    const approveButton = useMemo(() => {
        if (handling === null || handling === undefined) {
            return null;
        }

        if (terminal === null || terminal === undefined || terminal.id === undefined || !includes(['REEFER TERMINAL', 'VADO GATEWAY'], terminal?.name)) {
            return null;
        }

        if (canApprove) {
            return <Button disabled={fetchingApproval} color='primary' onClick={openPrompt}>{t('Approva')}</Button>
        }

        return null;

    }, [canApprove, t, handleSubmit, fetchingApproval, terminal, openPrompt, errors]);

    //if (qsaNotes === undefined || qsaNotes === null || qsaNotes === "") {
    //    return null;
    //}

    const description = useMemo(() => {
        if (handling === null || handling === undefined) {
            return null;
        }

        const { qsaNotes, bookingCheckResult, type, checkedIn } = handling;

        if (checkedIn === false && type === HandlingType.Boarding) {
            if (bookingCheckResult === null) {
                return <p>{t('In attesa di controllo booking.')}</p>
            }

            if (bookingCheckResult === false) {
                return <p>{t('Controllo booking non superato.')}</p>
            }
        }

        if (isApproved === false) {
            return checkedIn
                ? <p>{t('Non approvato.')}</p>
                : highRisk
                    ? <p>{t('In attesa di approvazione manuale.')}</p>
                    : <p>{t('Rischio sotto soglia, controllo booking non ancora effettuato.')}</p>
        }

        if (qsaNotes === null) {
            return <p>{t('Approvato automaticamente per rischio sotto soglia.')}</p>
        }

        return <>
            <p>{t('Approvato manualmente.')}</p>
            {qsaNotes !== '' && <QsaNotesText>{qsaNotes}</QsaNotesText>}
        </>;
    }, [isApproved, t, highRisk, handling]);

    if (fetching === true) {
        return <Loading />;
    }

    return <div>
        <h4 style={approveButton !== null ? { margin: 0 } : undefined}>{t('Approvazione')}</h4>
        {description}
        {approveButton}
        {prompt}
    </div>;

};

export default HandlingQsaApproval;